import { useState, type ReactNode } from 'react';
import { Layers, ListChecks, Square } from 'lucide-react';
import { BULK_OPERATIONS, isCancellable } from '@cp/shared';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  LoadMore,
  PageHeader,
  Select,
  StatusPill,
  TableSkeleton,
} from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { JobDrawer } from '@/components/jobs/JobDrawer';
import { BULK_RUN, BulkRunDrawer } from '@/components/bulk/BulkRunDrawer';
import { useBulkRuns } from '@/hooks/useBulk';
import { useRoles } from '@/hooks/useAuth';
import { useCancelJob, useJobs } from '@/hooks/useJobs';
import { useSystemSettings } from '@/hooks/useSystem';
import { toast } from '@/store/toastStore';
import type { JobDetail } from '@/types/api';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import { JOB_STATE, JOB_TYPE_LABELS, label } from '@/utils/labels';
import { can } from '@/utils/permissions';

/**
 * The job log.
 *
 * Every change to infrastructure passes through here, so this is the page an
 * operator opens to answer "did that actually happen". It refreshes on its own,
 * because a list of work in flight that only updates on reload is a list nobody
 * will trust.
 */

const FILTERS = [
  { value: '', label: 'Все состояния' },
  { value: 'queued', label: 'В очереди' },
  { value: 'running', label: 'Выполняются' },
  { value: 'retrying', label: 'Повторяются' },
  { value: 'completed', label: 'Завершены' },
  { value: 'failed', label: 'С ошибкой' },
  { value: 'cancelled', label: 'Отменены' },
];

/** The recent bulk runs (ADR-0046), above the jobs they are made of. */
function BulkRuns(): ReactNode {
  const runs = useBulkRuns();
  const [open, setOpen] = useState<string | null>(null);
  const list = runs.data ?? [];
  if (list.length === 0) return null;
  return (
    <Card flush title="Массовые действия" icon={Layers}>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Когда</th>
              <th scope="col">Действие</th>
              <th scope="col">Кто</th>
              <th scope="col">Сайты</th>
              <th scope="col">Состояние</th>
            </tr>
          </thead>
          <tbody>
            {list.slice(0, 5).map((run) => (
              <tr key={run.id} className="row-link" onClick={() => setOpen(run.id)} tabIndex={0} onKeyDown={(event) => event.key === 'Enter' && setOpen(run.id)}>
                <td className="nowrap">{formatRelative(run.createdAt)}</td>
                <td>{BULK_OPERATIONS[run.operation]?.label ?? run.operation}</td>
                <td>{run.createdBy}</td>
                <td className="nowrap">
                  {run.counts.done} из {Object.values(run.counts).reduce((sum, value) => sum + value, 0)}
                  {run.counts.failed > 0 ? <span className="tone-down"> · не удалось {run.counts.failed}</span> : null}
                  {run.counts.skipped > 0 ? <span className="muted"> · пропущено {run.counts.skipped}</span> : null}
                </td>
                <td>
                  <StatusPill map={BULK_RUN} status={run.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BulkRunDrawer id={open} onClose={() => setOpen(null)} />
    </Card>
  );
}

export function JobsPage(): ReactNode {
  const roles = useRoles();
  const [state, setState] = useState('');
  const [selected, setSelected] = useState<JobDetail | null>(null);
  const [cancelling, setCancelling] = useState<JobDetail | null>(null);

  // Page by page: a busy panel runs thousands of jobs a week, and the list
  // must neither stop at the first fifty nor load them all at once.
  const jobs = useJobs(state);
  const settings = useSystemSettings();

  const cancel = useCancelJob({
    onSuccess: () => {
      toast.success('Задача отменена');
      setCancelling(null);
      setSelected(null);
    },
  });

  // Queued and retrying jobs can be pulled back; a running one has to be asked
  // to stop and may already have changed something.
  const mayCancel = can(roles, 'jobs.cancel');
  const items = jobs.data?.pages.flatMap((page) => page.items) ?? [];
  const total = jobs.data?.pages[0]?.total ?? null;

  return (
    <div className="page">
      <PageHeader
        title="Задачи"
        subtitle={
          settings.data
            ? `Операции с сайтами и серверами. Завершённые задачи хранятся ${settings.data.jobRetentionDays} дн.`
            : 'Операции с сайтами и серверами'
        }
        actions={<Select label="Фильтр по состоянию" size="sm" value={state} onChange={setState} options={FILTERS} />}
      />

      <BulkRuns />

      <Card flush>
        {jobs.isPending ? (
          <TableSkeleton rows={6} columns={5} />
        ) : jobs.isError ? (
          <ErrorState error={jobs.error} />
        ) : items.length === 0 ? (
          <EmptyState icon={ListChecks} message={state === '' ? 'Задач пока нет' : 'Нет задач в этом состоянии'} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Операция</th>
                  <th scope="col">Состояние</th>
                  <th scope="col">Создана</th>
                  <th scope="col" className="numeric">
                    Попыток
                  </th>
                  <th scope="col">Причина отказа</th>
                  <th scope="col" className="shrink" />
                </tr>
              </thead>
              <tbody>
                {items.map((job) => (
                  <tr
                    key={job.id}
                    className="row-link"
                    tabIndex={0}
                    onClick={() => setSelected(job)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') setSelected(job);
                    }}
                  >
                    <td title={job.type}>{label(JOB_TYPE_LABELS, job.type)}</td>
                    <td>
                      <StatusPill map={JOB_STATE} status={job.state} />
                    </td>
                    <td className="muted nowrap" title={job.createdAt}>
                      {formatRelative(job.createdAt)}
                    </td>
                    <td className="numeric">{job.attempts}</td>
                    <td className="mono muted">{job.errorCode ?? '—'}</td>
                    <td className="shrink">
                      {mayCancel && isCancellable(job.state) ? (
                        <Button
                          size="sm"
                          variant="danger"
                          icon={Square}
                          onClick={(event) => {
                            event.stopPropagation();
                            setCancelling(job);
                          }}
                        >
                          Отменить
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <LoadMore
        shown={items.length}
        total={total}
        hasMore={jobs.hasNextPage}
        busy={jobs.isFetchingNextPage}
        onMore={() => void jobs.fetchNextPage()}
      />

      <JobDrawer job={selected} onClose={() => setSelected(null)} {...(mayCancel ? { onCancel: setCancelling } : {})} />

      <ConfirmDialog
        open={cancelling !== null}
        title="Отменить задачу?"
        description={cancelling ? `${label(JOB_TYPE_LABELS, cancelling.type)}. Задача ещё не начата.` : undefined}
        confirmLabel="Отменить задачу"
        cancelLabel="Оставить"
        destructive
        busy={cancel.isPending}
        error={cancel.isError ? errorText(cancel.error, 'Не удалось отменить задачу') : undefined}
        onConfirm={() => cancelling && cancel.mutate(cancelling.id)}
        onClose={() => setCancelling(null)}
      />
    </div>
  );
}
